//third-party
import { Row, Col, Container, Form, Alert, Button as BsButton } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";

//spotsync  
import { Facility } from "./Data/ApiTypes";
import { Input } from "./Controls/Input";
import { MediumPage } from "./Controls/MediumPage";
import { PageTitle } from "./Controls/PageTitle";
import { useParamIds } from "./Data/Common";
import { Button } from "./Controls/Button";
import { MoneyInput } from "./Controls/MoneyInput";
import { useState } from "react";
import { MonitorFacilityApiApi, ServerTransferMonitorPlanCreateReq } from "./shared/api/axios-client";

import { Select } from "./Controls/Select";
import { createApiService } from "./shared/apiConfig";
import { toast } from "react-toastify";
import { FEATURES, isFeatureEnabled } from "./Data/Features";
import { generateCouponCode } from "./FacilityPlanPriceAddModal";
import { HtmlEditor } from "./Controls/HtmlEditor";

const monitorApi = createApiService(MonitorFacilityApiApi)

export function FacilityPlanCreatePage() {
    const { facilityId, planEntryId } = useParamIds();
    const [loading, setLoading] = useState( false );
    const facility = useLoaderData() as Facility;
    const navigate = useNavigate();
    const verb = "Create";
    const { register, handleSubmit, watch, control, setValue, clearErrors, formState: { errors } } = useForm<ServerTransferMonitorPlanCreateReq>( {
        defaultValues: {
            facilityId: facility.facilityId,
            name: "",
            accessHours: "",
            description: "",
            dayOfMonthInvoice: 15,
            dayOfMonthDue: 1,
            dayOfMonthLate: 6,
            initialPrice: 150,
            prorate: 'Monthly',
            isOneTermPlan: false,
            renewalFailureDeactivateAfterDays: 0
         }
    } );

    const isRenewalEnabled = watch("renewalFailureDeactivateAfterDays") > 0;

    const onSubmit = async ( data: ServerTransferMonitorPlanCreateReq ) => {
        setLoading( true );
        if (!isRenewalEnabled) data.renewalFailureDeactivateAfterDays = 0;
        data.couponCode = generateCouponCode(null)
        monitorApi.createPlan({
            facilityId: facilityId,
            serverTransferMonitorPlanCreateReq: data
        }).then((res)=>{
            toast.success('Plan created successfully');
            navigate( `/facility/${facilityId}/plan` );
        }).catch((err)=>{
            toast.error(err);
        }).finally(()=>{
             setLoading( false );
        })
        
    };

    return <MediumPage>
        <div className="d-flex align-items-center gap-3">
            <BsButton variant="link" className="p-0" onClick={() => navigate(-1)}>← Back</BsButton>
            <PageTitle className="mb-0">
                {verb} Plan
            </PageTitle>
        </div>
        <form onSubmit={handleSubmit( onSubmit )}>
            <Input
                type="text"
                label="Name"
                {...register( "name", { required: "Name is required" } )}
                error={errors.name}
            />

            <Form.Group className="mb-3">
                <Form.Check
                    type="switch"
                    id="isPrivate"
                    label="Private Plan"
                    {...register("isPrivate")}
                />
                <Alert variant="info" className="mt-2 alert-light-bg">
                    Private plans are only visible to staff members and can be used to assign plans for <u>invited parkers only</u>. These are not visible from the public search results.
                    <div className="mt-2"><b>Important:</b> This cannot be changed after saving. </div>
                </Alert>
            </Form.Group>

            {isFeatureEnabled(facility, FEATURES.ONE_TERM_PARKING) && (
                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="isOneTermPlan"
                        label="One Term Plan (plan will not auto-renew)"
                        {...register("isOneTermPlan")}
                    />
                    <Alert variant="info" className="mt-2 alert-light-bg">
                        One-term plans expire after a single billing period. 
                        <div className="mt-2"><b>Important:</b> This cannot be changed after saving. </div>
                    </Alert>
                </Form.Group>
            )}

            <div className="two-col">
            <Input
                type="text"
                label="Description"
                as="textarea"
                rows={3}
                {...register("description", { required: "Description is required" })}
                error={errors.description}
            />
            </div>

            <Row className="mb-3">
            <Col md={4}>
                <Form.Label className="mb-1">Terms and Conditions</Form.Label>
            </Col>
            <Col md={9}>
                <Controller
                name="terms"
                control={control}
                rules={{ required: "Terms and Conditions is required" }}
                render={({ field }) => (
                    <HtmlEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Enter terms and conditions..."
                    isInvalid={!!errors.terms}
                    />
                )}
                />
                {errors.terms && (
                <Form.Text className="d-block text-danger ms-1">
                    {errors.terms.message}
                </Form.Text>
                )}
            </Col>
            </Row>

            <Row className="mb-3">
            <Col md={12}>
                <Form.Label className="mb-1">Approval Email Text</Form.Label>
                <div className="text-muted small">
                Add an optional message that will appear in the email parkers receive when their subscription is approved.
                </div>
            </Col>
            <Col md={9}>
                <Controller
                name="approvalEmailText"
                control={control}
                render={({ field }) => (
                    <HtmlEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Enter the approval email text..."
                    />
                )}
                />
            </Col>
            </Row>

            <Input
                type="text"
                label="Access Hours"
                {...register( "accessHours", { required: "Access hours are required" } )}
                error={errors.accessHours}
            />

            <Container fluid className="g-0">
                <Row>
                    <Col className="fw-bold h4">
                        Scheduling
                    </Col>
                </Row>
                <Row>
                    <Col xs={3}>
                        <Input
                            type="number"
                            label="Invoice Day"
                            explanation="Invoices are generated on this day of the month"
                            required
                            min={1} max={28}
                            disabled
                            {...register( "dayOfMonthInvoice", { valueAsNumber: true } )}
                            error={errors.dayOfMonthInvoice}
                        />
                    </Col>
                    <Col xs={3}>
                        <Input
                            type="number"
                            label="Due Day"
                            explanation="The first day they are allowed to park"
                            required
                            min={1} max={28}
                            disabled
                            {...register( "dayOfMonthDue", { valueAsNumber: true } )}
                            error={errors.dayOfMonthDue}
                        />
                    </Col>
                    <Col xs={3}>
                        <Input
                            type="number"
                            label="Late Fee Day"
                            explanation="The day late fees are accessed"
                            required
                            min={1} max={28}
                            disabled
                            {...register( "dayOfMonthLate", { valueAsNumber: true } )}
                            error={errors.dayOfMonthLate}
                        />
                    </Col>
                    <Col xs={3}>
                        <Select
                            label="Prorate"
                            explanation="Proration schema"
                            required
                            min={1} max={28}
                            {...register( "prorate")}
                            error={errors.prorate}
                        >
                            <option value={'Daily'}>Daily</option>
                            <option value={'Monthly'}>1/2 Monthly</option>
                            <option value="">None</option>
                        </Select>
                    </Col>
                </Row>
            </Container>

            <MoneyInput
                label="Initial Price"
                explanation="This Plan will be listed with this price.  Additional pricing options can be added later from the main Plan edit page."
                required
                {...register("initialPrice", { 
                    valueAsNumber: true,
                    min: { value: 0.01, message: "Price must be greater than 0" }
                })}
                error={errors.initialPrice}
            />

            <div className="border rounded p-3 mb-3">
                <div className="d-flex align-items-center gap-2 gap-md-4 flex-wrap">
                    <Form.Check
                        type="switch"
                        id="renewalFailureEnabled"
                        label="Deactivate subscription after failed payments"
                        checked={isRenewalEnabled}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue("renewalFailureDeactivateAfterDays", 7);
                            } else {
                                setValue("renewalFailureDeactivateAfterDays", 0);
                                clearErrors("renewalFailureDeactivateAfterDays");
                            }
                        }}
                    />
                    <Form.Group className="mb-0 d-flex align-items-center gap-2 ps-2 ps-md-3 border-start border-secondary">
                        <Form.Label htmlFor="renewalFailureDays" className="mb-0 text-nowrap">Days</Form.Label>
                        <Form.Control
                            id="renewalFailureDays"
                            type="number"
                            min={1}
                            disabled={!isRenewalEnabled}
                            className="text-end"
                            style={{ width: "4.5rem" }}
                            {...register("renewalFailureDeactivateAfterDays", {
                                valueAsNumber: true,
                                validate: (v) => v === 0 || v >= 1 || "Must be at least 1 day when enabled"
                            })}
                            isInvalid={!!errors.renewalFailureDeactivateAfterDays}
                        />
                    </Form.Group>
                </div>
                <Form.Text className="d-block text-muted ms-1 mt-1">
                    {!isRenewalEnabled
                        ? "After 3 failed daily payment attempts, the subscription will not be deactivated."
                        : "After 3 failed daily payment attempts, the subscription will be deactivated this many days after the start of the billing period."}
                </Form.Text>
                {errors.renewalFailureDeactivateAfterDays && (
                    <Form.Text className="d-block text-danger ms-1">
                        {errors.renewalFailureDeactivateAfterDays.message}
                    </Form.Text>
                )}

                {isRenewalEnabled && (
                    <Alert variant="info" className="alert-light-bg mb-0 mt-2">
                        <b>What this does:</b><br />
                        After 3 failed attempts to charge the card, the subscription
                        will be deactivated{" "}
                        <b>{watch("renewalFailureDeactivateAfterDays")} day(s)</b>{" "}
                        after the start of the billing period.
                    </Alert>
                )}
            </div>

            <div className="d-flex justify-content-end gap-2 mb-3">
                <Button className="w-50" loading={loading} onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button className="w-50 btn-main" loading={loading} type="submit">
                    Save Changes
                </Button>
            </div>
        </form>
    </MediumPage>;
}